import React from 'react';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';
import {
  CoreShopSettingsAuGstLayout,
  core_apimessages_TaxIdentification_TaxType as TaxType,
} from '../schemas/rql';
import FormSection from '@reverbdotcom/commons/src/components/form_section';
import { RCLoadingBars, RCAlertBox, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { InfoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import I18n from 'i18n-js';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import AustraliaGstForm, { AUSTRALIA_COUNTRY_CODE } from './australia_gst_form';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';

const VAT_HELP_ARTICLE_ID = '15478546545171';

type Props = ChildProps<any, CoreShopSettingsAuGstLayout.Query>;

export function ShopSettingsAuGstContainer({ data }: Props) {
  const vatHelpCenterUrl = useHelpCenterArticleUrl(VAT_HELP_ARTICLE_ID);
  const showTaxOnFeesAlert = useExpEnabled(experiments.SHOW_AU_TAX_ID_BANNER);
  const collectingSellerServicesTaxInAu = useExpEnabled(experiments.COLLECT_TAX_ON_AU_SELLER_FEES);

  const australiaGst = (data?.me?.shop?.taxIdentifications || []).find(taxIdentification => (
    taxIdentification.taxType === TaxType.GST && taxIdentification.countryCode == AUSTRALIA_COUNTRY_CODE
  ));

  const sellerTaxText = collectingSellerServicesTaxInAu ? 'auGstOnFeesDescription' : 'upcomingAuGstOnFeesDescription';

  const descriptionText = showTaxOnFeesAlert ? (
    <>
      <RCAlertBox
        type="info"
      >
        <RCTextWithIcon
          placement="left"
          svgComponent={InfoCircleIcon}
        >
          <span className="weight-bold"><Translate text={`discovery.shopSettings.australianGst.${sellerTaxText}`} /></span>
        </RCTextWithIcon>
        <Translate
          text="discovery.shopSettings.australianGst.addGstIdPrompt"
          tag="div"
        >
          <CoreLink to={vatHelpCenterUrl}><Translate text="discovery.shopSettings.australianGst.learnMore" /></CoreLink>
        </Translate>
      </RCAlertBox>
    </>
  ) : (
    <>
      <Translate text="discovery.shopSettings.australianGst.noGstMessage" />
      <CoreLink to={vatHelpCenterUrl} className="ml-space">
        <Translate text="discovery.shopSettings.australianGst.learnMore" />
      </CoreLink>
    </>
  );

  return (
    <FormSection
      title={I18n.t('discovery.shopSettings.australianGst.title')}
      description={descriptionText}
    >
      {data?.loading ? (
        <div className="d-flex fx-align-center fx-justify-center">
          <RCLoadingBars
            size="default"
          />
        </div>
      ) : (
        <AustraliaGstForm
          taxIdentification={australiaGst}
        />
      )}
    </FormSection>
  );
}

const LAYOUT_QUERY = gql`
  query Core_ShopSettings_AuGstLayout {
    me {
      uuid
      shop {
        _id
        taxIdentifications {
          id
          taxId
          taxType
          status
          countryCode
        }
      }
    }
  }
`;

const withQuery = withGraphql<any, CoreShopSettingsAuGstLayout.Query>(
  LAYOUT_QUERY,
);

export default withQuery(ShopSettingsAuGstContainer);
